<script setup lang="ts">
import {
  instanceOfEnumerationValueDto,
  instanceOfEnumerationValueListDto,
  instanceOfTextValueDto,
  ISingleSystemSetupParameter,
  TaskTypeDescription,
  instanceOfNumericListDto,
  instanceOfTextValueListDto,
} from '@/api/generator';
import VeeEnumerationValue from '@/components/forms/dataTypes/VeeEnumerationValue.vue';
import VeeEnumerationValueList from '@/components/forms/dataTypes/VeeEnumerationValueList.vue';

import { FormMode } from '@/components/forms/types.ts';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { hasTranslationKey } from '@/utils/helper.ts';
import { I18nKey } from '@/utils/typings.ts';
import { useFieldArray, useFieldValue, useSetFieldValue } from 'vee-validate';
import VeeTextValue from '../../../components/forms/dataTypes/VeeTextValue.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import VeeNumericList from '@/components/forms/dataTypes/VeeNumericList.vue';
import VeeTextValueList from '@/components/forms/dataTypes/VeeTextValueList.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();

type QaSectionGroupParameterProps = {
  name: string;
};
const props = defineProps<QaSectionGroupParameterProps>();
const mode = useFormMode();
const taskType = useFieldValue<TaskTypeDescription>('taskType');

const parameter = useFieldValue<ISingleSystemSetupParameter>(props.name);
const setParameterEnabledState = useSetFieldValue<boolean>(`${props.name}.isEnabled`);

const { remove: removeFieldValue, fields: fieldValues } = useFieldArray(`${props.name}.values`);

function getMode(item?: ISingleSystemSetupParameter): FormMode {
  if (
    item &&
    instanceOfEnumerationValueDto(item) &&
    item.options?.length &&
    item.options?.length > 1
  ) {
    return mode.value;
  }

  return 'details';
}

const removeButtonHandler = () => {
  setParameterEnabledState(false);
  for (let i = fieldValues.value.length - 1; i >= 0; i--) {
    removeFieldValue(i);
  }
};

function getParameterLabel(param: ISingleSystemSetupParameter): string {
  const name = param.name;
  const geometryName = 'geometry' in param ? (param.geometry?.name ?? '') : '';
  const translated = name && hasTranslationKey(name) ? t(name as I18nKey) : name;
  return `${translated} ${geometryName}`.trim();
}
</script>

<template>
  <v-card v-if="parameter.isEnabled" border class="single-setup-parameter">
    <v-card-item class="pa-0">
      <v-card-title class="parameter-title">
        <IdentifierLabel :label="getParameterLabel(parameter)" :identifier="parameter.iuid" />

        <VSpacer />
        <PtwDeleteButton
          v-if="mode !== 'details' && taskType === 'ManualTask'"
          @delete="removeButtonHandler"
        />
      </v-card-title>
    </v-card-item>
    <v-card-text>
      <VeeEnumerationValueList
        v-if="parameter && instanceOfEnumerationValueListDto(parameter)"
        :name="name"
        :mode="mode"
      />
      <VeeEnumerationValue
        v-if="parameter && instanceOfEnumerationValueDto(parameter)"
        :name="name"
        :mode="getMode(parameter)"
      />
      <VeeTextValue
        v-if="parameter && instanceOfTextValueDto(parameter)"
        :name="name"
        :mode="mode"
        :placeholder="
          parameter.name === 'TextNameLabelEnum.IndexingPoint'
            ? $t('PlaceholderCouchIndexingPoint')
            : undefined
        "
      />

      <VeeNumericList
        v-if="instanceOfNumericListDto(parameter)"
        :name="name"
        :mode="mode"
        :deleteTooltipText="`${$t('Remove')} ${$t(parameter.name as I18nKey)}`"
        :addTooltipText="`${$t('Add')} ${$t(parameter.name as I18nKey)}`"
      />

      <VeeTextValueList
        v-if="instanceOfTextValueListDto(parameter)"
        :name="name"
        :mode="mode"
        :deleteTooltipText="`${$t('Remove')} ${$t(parameter.name as I18nKey)}`"
        :addTooltipText="`${$t('Add')} ${$t(parameter.name as I18nKey)}`"
      />
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>
.single-setup-parameter {
  overflow: visible !important;
}
</style>
